import { useEffect, useState } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import {
  Loader2,
  ListChecks,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface ReferenceGroup {
  reference: string;
  total: number;
  count: number;
  bankPaymentIds: number[];
}

interface DayGroup {
  day: string;
  total: number;
  count: number;
  referenceCount: number;
  references: ReferenceGroup[];
}

interface BatchResponse {
  days: DayGroup[];
  grandTotal: number;
}

const gbp = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

function formatDay(day: string): string {
  if (day === "unknown") return "Unknown date";
  const d = new Date(`${day}T00:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function CashReceiptsDailyBatchPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [data, setData] = useState<BatchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) setLocation("/login");
  }, [authLoading, user, setLocation]);

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString();
      const res = await fetch(
        `${BASE}/api/cash-receipts/daily-batch-reconciliation${qs ? `?${qs}` : ""}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: BatchResponse = await res.json();
      setData(json);
      // Expand every day by default so the references are visible at a glance.
      const next: Record<string, boolean> = {};
      for (const d of json.days) next[d.day] = true;
      setExpanded(next);
    } catch (err: any) {
      setError(err?.message ?? "Failed to load daily batch reconciliation");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;

  const days = data?.days ?? [];

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ListChecks className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-slate-900">Daily Batch Reconciliation</h1>
          </div>
          <Button onClick={fetchData} disabled={loading} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-4 max-w-3xl">
          Totals of everything posted to Merlin from the Cash Receipts page, grouped by the day it
          was posted and by the BACS reference used. Each reference total should match the sum of
          the matching nominal-batch lines in Merlin for that reference.
        </p>

        <div className="flex flex-wrap gap-3 mb-5 items-end">
          <div>
            <label className="text-xs text-slate-600 block mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <Button onClick={fetchData} disabled={loading} size="sm">
            Apply
          </Button>
          {(from || to) && (
            <Button
              onClick={() => {
                setFrom("");
                setTo("");
                setTimeout(fetchData, 0);
              }}
              variant="ghost"
              size="sm"
            >
              Clear
            </Button>
          )}
          {data && (
            <div className="ml-auto text-right">
              <div className="text-xs text-slate-500">Grand total</div>
              <div className="text-lg font-bold text-slate-900 tabular-nums">
                {gbp(data.grandTotal)}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : days.length === 0 ? (
          <div className="text-center py-20 text-slate-400 text-sm">
            No posted cash receipts found for this range.
          </div>
        ) : (
          <div className="space-y-4">
            {days.map((d) => {
              const open = expanded[d.day] ?? false;
              return (
                <div
                  key={d.day}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                >
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [d.day]: !p[d.day] }))}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                  >
                    {open ? (
                      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{formatDay(d.day)}</div>
                      <div className="text-xs text-slate-500">
                        {d.referenceCount} reference{d.referenceCount === 1 ? "" : "s"} ·{" "}
                        {d.count} line{d.count === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Day total</div>
                      <div className="font-bold text-slate-900 tabular-nums">{gbp(d.total)}</div>
                    </div>
                  </button>

                  {open && (
                    <div className="border-t border-slate-100">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-slate-500 bg-slate-50">
                            <th className="text-left font-medium px-4 py-2">Reference</th>
                            <th className="text-right font-medium px-4 py-2">Lines</th>
                            <th className="text-left font-medium px-4 py-2">Bank payment(s)</th>
                            <th className="text-right font-medium px-4 py-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.references.map((r) => (
                            <tr
                              key={r.reference}
                              className="border-t border-slate-100 hover:bg-slate-50"
                            >
                              <td className="px-4 py-2 font-mono text-xs text-slate-800">
                                {r.reference}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                                {r.count}
                              </td>
                              <td className="px-4 py-2 text-xs text-slate-500">
                                {r.bankPaymentIds.map((id) => `BP${id}`).join(", ")}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums font-medium text-slate-900">
                                {gbp(r.total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
